import React, { useCallback } from 'react';
import { Divider as MuiDivider, List } from '@mui/material';
import { motion } from 'framer-motion';
import {
  DividerSignal,
  isDividerSignal,
  isParentNav,
  NavItemData
} from '@/config';
import { NavigationItem } from './NavigationItem';
import { useCurrentHospital } from '@/modules/appContext';
import { useUpdateUserAppSettingsMutation, useUserAppSetting } from '@/modules';
import { styled } from '@mui/material/styles';
import { useCleanPathname } from '@ianesthesie-kit/core';

const variants = {
  open: {
    transition: { delayChildren: 0.1 }
  }
};

export interface NavigationListProps {
  navItems: (NavItemData | DividerSignal)[];
}

export const NavigationList = ({ navItems }: NavigationListProps) => {
  const pathname = useCleanPathname();
  const {
    isUnstable,
    queryResult: { currentData: hospital }
  } = useCurrentHospital();
  const newRoutesViewed = useUserAppSetting('newRoutesViewed', []);
  const [updateSettings] = useUpdateUserAppSettingsMutation();

  const onAfterClick = useCallback(
    (n: NavItemData) => {
      const path = isParentNav(n) ? n.parentPath : n.path;
      if (!newRoutesViewed.includes(path)) {
        updateSettings({
          newRoutesViewed: newRoutesViewed.concat(path)
        });
      }
    },
    [updateSettings, newRoutesViewed]
  );

  const getShowNewBadge = (n: NavItemData) =>
    !!n.isNew &&
    !newRoutesViewed.includes(isParentNav(n) ? n.parentPath : n.path);

  return (
    <List component={motion.ul} variants={variants}>
      {navItems.map((nav, i) => {
        if (isDividerSignal(nav)) {
          // eslint-disable-next-line react/no-array-index-key
          return <Divider key={i} />;
        }
        const path = isParentNav(nav) ? nav.parentPath : nav.path;
        return (
          <NavigationItem
            key={path}
            navItem={nav}
            currentRoute={pathname}
            currentHospital={hospital}
            isEvaluatingPermissions={!!nav.isEnabled && isUnstable}
            showNewBadge={getShowNewBadge(nav)}
            onAfterClick={onAfterClick}
          />
        );
      })}
    </List>
  );
};

const Divider = styled(MuiDivider)`
  margin: 8px 0;
  &:last-child {
    display: none;
  }
`;
